import React, { useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { colors, Tooltip, Typography } from "@material-ui/core";
import clsx from "clsx";
import { InfoOutlined } from "@material-ui/icons";

import typography from "../theme/typography";
import { riskDisplayName, WebTrackerRiskLevel } from "./types";

export type RiskProps = {
  description: string | JSX.Element;
  riskDisplayNameOverride?: string | null;
  riskLevel: WebTrackerRiskLevel;
};

const useStyles = makeStyles((_) => ({
  riskChip: {
    borderRadius: "0.25em",
    color: "white",
    display: "inline-block",
    padding: "0.375em 0.5em 0.25em 0.75em",
    fontWeight: 500,
    textTransform: "uppercase",
    fontSize: typography.body2.fontSize,
    lineHeight: typography.body2.fontSize,
  },
  riskChipLow: {
    backgroundColor: colors.green[700],
  },
  riskChipMedium: {
    backgroundColor: colors.yellow[700],
  },
  riskChipUnknown: {
    backgroundColor: colors.grey[500],
  },
  riskChipHigh: {
    backgroundColor: colors.red[700],
  },
  riskChipRemoved: {
    backgroundColor: colors.grey[100],
    color: "black",
    borderColor: "black",
  },
  riskChipIcon: {
    display: "inline",
    fontSize: typography.body1.fontSize,
    marginTop: "-2px",
    marginLeft: "5px",
  },
}));

function Risk({
  description,
  riskLevel,
  riskDisplayNameOverride = null,
}: RiskProps) {
  const classes = useStyles();
  const riskLevelClass = useMemo(() => {
    switch (riskLevel) {
      case "high":
        return classes.riskChipHigh;
      case "medium":
        return classes.riskChipMedium;
      case "low":
        return classes.riskChipLow;
      case "removed":
        return classes.riskChipRemoved;
      case "unknown":
      default:
        return classes.riskChipUnknown;
    }
  }, [classes, riskLevel]);

  const tooltipTitle =
    typeof description === "string" ? (
      <Typography component="p" variant="body1" style={{ color: "white" }}>
        {description}
      </Typography>
    ) : (
      description
    );

  return (
    <Tooltip title={tooltipTitle} style={{ cursor: "pointer" }}>
      <span className={clsx(classes.riskChip, riskLevelClass)}>
        {riskDisplayNameOverride !== null ? (
          <span>{riskDisplayNameOverride}</span>
        ) : (
          <span>
            {riskDisplayName(riskLevel)}{" "}
            {riskLevel === "removed" ? "" : " RISK"}
          </span>
        )}
        <InfoOutlined className={clsx(classes.riskChipIcon)} style={{}} />
      </span>
    </Tooltip>
  );
}

export default Risk;
